import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { listApiKeys } from '../../lib/api-client';
import { ensureOneApiKey, type HeldApiKey } from '../../lib/ensure-api-key';

/**
 * In-memory-only store for the two API keys the onboarding flow mints right
 * after org creation (TTP-243 architecture note #3 in the issue): a
 * `trv_test_` (sandbox) and a `trv_live_` (production) key. Deliberately a
 * React Context, not `localStorage` — CLAUDE.md domain rule #4 / the issue's
 * UI rule "segredos exibidos uma vez": the raw key value must never touch
 * persistent storage. This context is the ONLY place in the app that ever
 * holds a raw key string outside of the single fetch call that used it.
 *
 * Why raw keys, not just metadata: `request.mode` on the backend comes
 * exclusively from which key authenticated the call (never the request
 * body) — see providers.service.ts#putCredentials's `isSandbox` branch. So
 * Tela 3 (real credential validation, must hit Secret Manager) MUST send
 * the `trv_live_` key, and a sandbox-only demo quote MUST send the
 * `trv_test_` key. Only having the metadata (id/prefix) is useless for
 * this — the plaintext is what goes in the Authorization header.
 */

/** Alias kept for this file's existing call sites/tests — same shape as `ensure-api-key.ts`'s `HeldApiKey`. */
export type OnboardingApiKey = HeldApiKey;

export interface OnboardingApiKeys {
  test: OnboardingApiKey | null;
  live: OnboardingApiKey | null;
}

const EMPTY_KEYS: OnboardingApiKeys = { test: null, live: null };

/**
 * Fixed names used to find-or-mint the onboarding pair idempotently (see
 * `ensureKeys` below). Chosen to be human-recognizable in a future "API
 * keys" management screen (TTP-244, out of scope here) — not just
 * internal plumbing.
 */
const SANDBOX_KEY_NAME = 'onboarding — sandbox';
const LIVE_KEY_NAME = 'onboarding — produção';

interface ApiKeysContextValue {
  keys: OnboardingApiKeys;
  /** `true` while a mint/reuse round-trip is in flight — steps 3/4/5 should show a loading state instead of rendering with `keys` still null. */
  loading: boolean;
  error: string | null;
  /**
   * Idempotently ensures both keys exist and are held in memory, minting
   * whichever is missing. Safe to call from multiple step components (each
   * mounts fresh on route change) — concurrent/duplicate calls within the
   * same page load are deduped via `inFlightRef`; a call after a real
   * browser reload (this context reset, keys lost) detects any leftover
   * "onboarding — *" keys from the previous mint via `GET
   * /internal/api-keys` and REVOKES them before minting a fresh replacement
   * pair, rather than literally reusing them — an orphaned key's raw value
   * is gone forever (ApiKeysService#create returns `key` exactly once,
   * apps/api/src/modules/auth/api-keys.service.ts), so "reuse" can only
   * mean "don't let it pile up as a permanently-unusable active key";
   * replacing it 1:1 on the rare reload-mid-onboarding path keeps the
   * per-mode active-key count bounded instead of growing on every reload
   * (the concern the architecture note flags: "evita acumular chaves
   * órfãs").
   */
  ensureKeys: (idToken: string) => Promise<OnboardingApiKeys>;
  reset: () => void;
}

const ApiKeysContext = createContext<ApiKeysContextValue | null>(null);

export function ApiKeysProvider({ children }: { children: ReactNode }) {
  const [keys, setKeys] = useState<OnboardingApiKeys>(EMPTY_KEYS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const keysRef = useRef(keys);
  keysRef.current = keys;
  const inFlightRef = useRef<Promise<OnboardingApiKeys> | null>(null);

  const ensureKeys = useCallback(async (idToken: string): Promise<OnboardingApiKeys> => {
    if (inFlightRef.current) {
      return inFlightRef.current;
    }

    const run = (async () => {
      setLoading(true);
      setError(null);
      try {
        const existingMetadata = await listApiKeys(idToken);
        const [test, live] = await Promise.all([
          ensureOneApiKey(idToken, 'test', SANDBOX_KEY_NAME, keysRef.current.test, existingMetadata),
          ensureOneApiKey(idToken, 'live', LIVE_KEY_NAME, keysRef.current.live, existingMetadata),
        ]);
        const next: OnboardingApiKeys = { test, live };
        setKeys(next);
        return next;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Falha ao preparar as chaves de API do onboarding.');
        throw err;
      } finally {
        setLoading(false);
        inFlightRef.current = null;
      }
    })();

    inFlightRef.current = run;
    return run;
  }, []);

  const reset = useCallback(() => {
    setKeys(EMPTY_KEYS);
    setError(null);
  }, []);

  const value = useMemo<ApiKeysContextValue>(
    () => ({ keys, loading, error, ensureKeys, reset }),
    [keys, loading, error, ensureKeys, reset],
  );

  return <ApiKeysContext.Provider value={value}>{children}</ApiKeysContext.Provider>;
}

export function useApiKeys(): ApiKeysContextValue {
  const ctx = useContext(ApiKeysContext);
  if (!ctx) {
    throw new Error('useApiKeys must be used within an ApiKeysProvider');
  }
  return ctx;
}
